'use client';

import { Trip } from '@/lib/trips/api';
import { cn } from '@/lib/utils';

interface TripCardProps {
  trip: Trip;
  onSelect?: (trip: Trip) => void;
  className?: string;
  selected?: boolean;
}

export default function TripCard({ trip, onSelect, className, selected }: TripCardProps) {
  const formatPrice = (price: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getTripTypeStyles = (type: 'Join-In' | 'Private') => {
    return type === 'Join-In'
      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
      : 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
  };

  // Generate placeholder SVG as data URI
  const getPlaceholderImage = (name: string) => {
    const colors = ['3B82F6', '8B5CF6', 'EC4899', '10B981', 'F59E0B', 'EF4444'];
    const color = colors[name.length % colors.length];
    const initials = name.substring(0, 2).toUpperCase();
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="250" viewBox="0 0 400 250"><rect fill="#${color}" width="400" height="250"/><text fill="white" font-family="Arial,sans-serif" font-size="48" font-weight="bold" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">${initials}</text></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  };

  const imageUrl = trip.image_url || getPlaceholderImage(trip.name);

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-xl bg-white shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:bg-dark-2',
        selected && 'ring-2 ring-primary',
        className
      )}
    >
      {/* Thumbnail */}
      <div className="relative h-48 overflow-hidden bg-gray-100 dark:bg-dark">
        <img
          src={imageUrl}
          alt={trip.name}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            (e.target as HTMLImageElement).src = getPlaceholderImage(trip.name);
          }}
        />
        
        {/* Trip Type Badge */}
        <div className="absolute left-3 top-3">
          <span
            className={cn(
              'rounded-full px-3 py-1 text-xs font-semibold',
              getTripTypeStyles(trip.trip_type)
            )}
          >
            {trip.trip_type}
          </span>
        </div>

        {/* Discount Badge */}
        {trip.discount_percent && trip.discount_percent > 0 && (
          <div className="absolute right-3 top-3">
            <span className="rounded-full bg-red-500 px-3 py-1 text-xs font-semibold text-white">
              {Math.round(trip.discount_percent)}% OFF
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Name */}
        <h3 className="mb-2 line-clamp-1 text-lg font-semibold text-dark dark:text-white">
          {trip.name}
        </h3>

        {/* Location */}
        <div className="mb-3 flex items-center text-sm text-gray-500 dark:text-gray-400">
          <svg
            className="mr-1.5 h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          {trip.location?.location_name || 'Location not specified'}
        </div>

        {/* Duration */}
        <div className="mb-4 flex items-center text-sm text-gray-500 dark:text-gray-400">
          <svg
            className="mr-1.5 h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {trip.days}D / {trip.nights}N
        </div>

        {/* Price, Stars and Select Button */}
        <div className="flex items-center justify-between border-t border-stroke pt-4 dark:border-dark-3">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">From</p>
            <p className="text-xl font-bold text-primary">
              {formatPrice(trip.pricing?.adult_price || 0, trip.pricing?.currency)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">per adult</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Stars Rating */}
            {trip.stars > 0 && (
              <div className="flex items-center">
                <svg className="h-4 w-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="ml-1 text-sm font-medium text-gray-600 dark:text-gray-300">
                  {trip.stars.toFixed(1)}
                </span>
              </div>
            )}
            
            {onSelect && (
              <button
                onClick={() => onSelect(trip)}
                className={cn(
                  'rounded-lg px-5 py-2.5 text-sm font-medium transition-colors',
                  selected
                    ? 'bg-primary text-white'
                    : 'bg-primary/10 text-primary hover:bg-primary hover:text-white dark:bg-primary/20'
                )}
              >
                {selected ? 'Selected' : 'Select'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
